import {
  assertInInjectionContext,
  DestroyRef,
  effect,
  inject,
  Signal,
  signal,
  untracked
} from "@angular/core";

import { Observable, Subject } from "rxjs";

import { sharedEnvironment } from "../../../environments/shared-environment";
import { EventStreamFrame, parseEventStream } from "../../utils/event-stream-parser.util";
import { LektoAuthService } from "../essential/lekto-auth.service";
import { ImpersonationService } from "../identity/impersonation.service";

const INITIAL_RETRY_MS = 1_000;
const MAX_RETRY_MS = 30_000;
const MAX_RETRY_EXPONENT = 5;
const JITTER_RATIO = 0.2;

export interface AuthenticatedEventStreamOptions {
  /** Caminho sob `sharedEnvironment.serviceUrl`, sem barra inicial (ex.: `api/notifications/stream`). */
  readonly path: string;
  /**
   * Recebe cada frame da conexão vigente, heartbeat incluído: filtrar o evento e validar o `data` é
   * do consumidor. Frame de uma conexão já superada nunca chega aqui.
   */
  readonly onFrame: (frame: EventStreamFrame) => void;
}

/** Canal SSE autenticado do monorepo (ARCHITECTURE.md, "Tempo real por SSE"). */
export interface AuthenticatedEventStream {
  /** `true` enquanto uma conexão está aberta e lendo. */
  readonly connected: Signal<boolean>;
  /**
   * `false` = o canal não existe para esta sessão: 403 de papel ou personificação recusados, 404 de produto fora
   * da allowlist (notificações) ou de feature desligada (importação) — decididos pelo serviço do stream, fora do Ocelot.
   */
  readonly available: Signal<boolean>;
  /** Emitido a cada conexão aberta. Não há replay: o consumidor recarrega o que exibe. */
  readonly resync: Observable<void>;
  /** Emitido quando a identidade muda (troca de perfil ou personificação). */
  readonly identityChanged: Observable<void>;
  start(): void;
  stop(): void;
}

/**
 * Conexão SSE autenticada, extraída do canal de notificações (DEV-872) para servir a qualquer
 * consumidor.
 *
 * Por que `fetch` e não `EventSource`: o token tem de ir no header `Authorization`, e o
 * `EventSource` nativo não permite headers. Consequência: esta chamada NÃO passa pelos
 * interceptors, então `Authorization` e `X-Current-Role` são montados aqui, do mesmo jeito que o
 * `customHttpInterceptor` monta.
 *
 * Por que `getReader()` + `TextDecoder` e não `pipeThrough(new TextDecoderStream())`: o ambiente de
 * teste do repo não tem `TextDecoderStream` nem `ReadableStream`. `globalThis.fetch` com guarda
 * segue o precedente do `IntersectionObserver` na `infinite-scroll.directive.ts`.
 *
 * Contrato de confiabilidade: a listagem HTTP do consumidor é a fonte de verdade; este canal é
 * best-effort. Queda inesperada é NORMAL — o servidor derruba a conexão saturada de propósito — e
 * não é estado de erro visível ao usuário.
 */
class AuthenticatedEventStreamConnection implements AuthenticatedEventStream {
  private readonly authService = inject(LektoAuthService);
  private readonly impersonationService = inject(ImpersonationService);
  private readonly options: AuthenticatedEventStreamOptions;

  private readonly _connected = signal<boolean>(false);
  private readonly _available = signal<boolean>(true);
  private readonly resyncSubject = new Subject<void>();
  private readonly identityChangedSubject = new Subject<void>();

  public readonly connected: Signal<boolean> = this._connected.asReadonly();
  public readonly available: Signal<boolean> = this._available.asReadonly();
  public readonly resync: Observable<void> = this.resyncSubject.asObservable();
  public readonly identityChanged: Observable<void> = this.identityChangedSubject.asObservable();

  private controller: AbortController | null = null;
  private retryTimer: ReturnType<typeof globalThis.setTimeout> | null = null;
  private retryAttempt = 0;
  private running = false;
  /** 401: para de tentar sem esconder a feature. Rearmado por troca de identidade. */
  private suspended = false;
  private identityWatchArmed = false;

  public constructor(options: AuthenticatedEventStreamOptions) {
    this.options = options;
    // Personificação e troca de perfil trocam o token e o `X-Current-Role`. Manter o stream aberto
    // faria esta sessão receber dados de outra identidade — inaceitável num produto que trata dado
    // de menor de idade. `authProfile` é escrito no `createProfileCookie` da troca de perfil.
    effect(() => {
      this.authService.authProfile();
      this.impersonationService.isImpersonating();

      // O corpo de ação não pode ficar rastreado: `connect()` lê `_available` (e, via `currentRole()`
      // → `getProfileData()` → `productConfig.cookieName()`, outro `computed()`) na parte síncrona
      // antes do primeiro `await` — que roda dentro do mesmo ciclo reativo deste effect, porque
      // `restart()` chama `connect()` sem `await`. Sem `untracked`, `_available` vira dependência do
      // effect; como `connect()` também ESCREVE `_available` no 403/404, um papel fora da allowlist
      // gera laço: available=false → effect suja → restart() → available=true → connect() → 403 → …
      untracked(() => {
        if (!this.identityWatchArmed) {
          this.identityWatchArmed = true;
          return;
        }
        // A identidade mudou: o veredito de disponibilidade da identidade anterior não vale mais,
        // rodando ou não — senão um `start()` posterior herda `available`/`suspended` de outra
        // identidade (ver `start()`).
        this.suspended = false;
        this._available.set(true);
        // Avisa o consumidor para descartar todo o conteúdo da identidade anterior — mesmo motivo
        // pelo qual a conexão é derrubada e reaberta.
        this.identityChangedSubject.next();
        if (this.running) {
          this.restart();
        }
      });
    });

    inject(DestroyRef).onDestroy(() => this.stop());
  }

  public start(): void {
    if (this.running) {
      return;
    }
    this.running = true;
    this.suspended = false;
    // Rearma a disponibilidade: um `stop()` seguido de `start()` (troca de perfil, remontagem) tem
    // de poder descobrir de novo se esta identidade recebe o canal, em vez de herdar o
    // `available = false` de uma identidade anterior.
    this._available.set(true);
    this.retryAttempt = 0;
    void this.connect();
  }

  public stop(): void {
    this.running = false;
    this.clearRetry();
    // abortCurrent() já limpa `connected` — é a única fonte desse clear (ver comentário lá).
    this.abortCurrent();
  }

  private restart(): void {
    this.clearRetry();
    this.abortCurrent();
    this.suspended = false;
    this._available.set(true);
    this.retryAttempt = 0;
    void this.connect();
  }

  private async connect(): Promise<void> {
    if (!this.running || this.suspended || !this._available()) {
      return;
    }
    // Ambiente sem fetch (jsdom): não há canal a abrir, e não pode explodir.
    if (typeof globalThis.fetch === "undefined") {
      return;
    }

    const tokenData = this.authService.getTokenData();
    if (!tokenData?.accessToken) {
      // Cookie ilegível no instante exato deste connect() (corrida de troca de identidade, ou
      // sessão ainda montando): todo OUTRO caminho não-terminal deste método ou agenda retry ou
      // marca uma flag terminal (403/404/401). Sem isto, o canal morreria em silêncio até a
      // próxima troca de identidade. O lado seguro é tratar token ilegível como transitório.
      this.scheduleRetry();
      return;
    }

    // Um retry ou uma reentrada não podem deixar a conexão anterior lendo sem dono.
    this.abortCurrent();

    const controller = new AbortController();
    this.controller = controller;

    try {
      const response = await globalThis.fetch(
        `${sharedEnvironment.serviceUrl}/${this.options.path}`,
        {
          headers: this.buildHeaders(tokenData.tokenType, tokenData.accessToken),
          signal: controller.signal
        }
      );

      // Conexão superada enquanto o fetch estava em voo (restart(), troca de identidade, stop()):
      // nada abaixo pode tocar estado global, ou o veredito de disponibilidade (ou o `resync`) de
      // uma identidade vaza para a seguinte — mesmo caminho que o `readLoop` guarda por frame.
      if (this.controller !== controller) {
        return;
      }

      // 403 = papel ou personificação recusados; 404 = produto fora da allowlist (notificações) ou feature
      // desligada (importação), inclusive com a página aberta: a reconexão é recusada. Os dois são decididos pelo
      // serviço do stream, fora do Ocelot. Nos dois casos o canal não existe para esta sessão: para de vez, sem retry.
      if (response.status === 403 || response.status === 404) {
        this._available.set(false);
        return;
      }
      // Não há refresh token no monorepo; o logout vem de qualquer outra chamada, via interceptor.
      if (response.status === 401) {
        this.suspended = true;
        return;
      }
      if (!response.ok || !response.body) {
        this.scheduleRetry();
        return;
      }

      this.retryAttempt = 0;
      this._connected.set(true);
      this.resyncSubject.next();

      await this.readLoop(response.body.getReader(), controller);
    } catch {
      // Abort deliberado não é erro, e queda inesperada é comportamento esperado do servidor.
    } finally {
      // Epílogo de um connect() que já foi superado por outro (restart(), ou uma reentrada) não pode
      // agendar retry: órfã a conexão vigente (o próximo connect() sobrescreveria `this.controller`
      // sem abortá-la). O clear de `connected` NÃO mora aqui dentro do guard: se morasse, um epílogo
      // tardio de um connect() já superado pularia o guard inteiro e nunca limparia nada — travando
      // `connected` em `true` quando o dono legítimo (`stop()`, `restart()`) já trocou
      // `this.controller` antes deste epílogo rodar. Por isso o clear vive em `abortCurrent()`.
      if (this.controller === controller) {
        this.abortCurrent();
        if (this.running && !this.suspended && this._available()) {
          this.scheduleRetry();
        }
      }
    }
  }

  private async readLoop(
    reader: ReadableStreamDefaultReader<Uint8Array>,
    controller: AbortController
  ): Promise<void> {
    const decoder = new TextDecoder();
    let buffer = "";

    for (;;) {
      const { value, done } = await reader.read();
      // Uma leitura já resolvida não é cancelada por `abort()`: ele só rejeita a PRÓXIMA leitura.
      // Sem esta checagem, um frame da identidade anterior seria entregue depois do reset (troca de
      // identidade, restart(), stop()). `this.controller !== controller` cobre também uma conexão
      // nova já ter assumido no meio do caminho.
      if (done || this.controller !== controller) {
        return;
      }
      // `stream: true` preserva o estado do decodificador entre chunks: caractere multibyte partido
      // no meio do chunk não vira "�".
      const text = value ? decoder.decode(value, { stream: true }) : "";
      const parsed = parseEventStream(buffer, text);
      buffer = parsed.rest;
      for (const frame of parsed.frames) {
        this.options.onFrame(frame);
      }
    }
  }

  private buildHeaders(tokenType: string, accessToken: string): Record<string, string> {
    const headers: Record<string, string> = {
      Authorization: `${tokenType} ${accessToken}`,
      Accept: "text/event-stream"
    };
    const role = this.currentRole();
    if (role) {
      headers["X-Current-Role"] = role;
    }
    return headers;
  }

  private currentRole(): string {
    // `getProfileData()` faz JSON.parse do cookie e lança quando ele não existe.
    try {
      return this.authService.getProfileData()?.coRole ?? "";
    } catch {
      return "";
    }
  }

  private scheduleRetry(): void {
    if (this.retryTimer !== null) {
      return;
    }
    const base = Math.min(MAX_RETRY_MS, INITIAL_RETRY_MS * 2 ** this.retryAttempt);
    // O jitter desalinha as reconexões entre os navegadores: sem ele, um restart do servidor faz
    // todas as abas perderem a conexão no mesmo instante e voltarem em ondas sincronizadas.
    //
    // `Math.random()` de propósito, e não `crypto.getRandomValues()`: o Sonar marca todo uso de
    // PRNG (`typescript:S2245`), mas o escopo da regra é valor cuja imprevisibilidade importa
    // (chave, token, segredo). Aqui o sorteio vira um `setTimeout` de 0 a 20% de `base` e nunca sai
    // deste método. Revisado e aceito no Sonar em 20/08/2026 (DEV-872).
    const jitter = Math.round(base * JITTER_RATIO * Math.random());
    this.retryAttempt = Math.min(this.retryAttempt + 1, MAX_RETRY_EXPONENT);

    this.retryTimer = globalThis.setTimeout(() => {
      this.retryTimer = null;
      void this.connect();
    }, base + jitter);
  }

  private clearRetry(): void {
    if (this.retryTimer !== null) {
      globalThis.clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }

  private abortCurrent(): void {
    this.controller?.abort();
    this.controller = null;
    // Vive aqui (e não no epílogo do finally) porque só os donos legítimos de uma transição de
    // conexão chamam este método (`stop()`, `restart()`, reentrada no topo de `connect()`) — nunca
    // um epílogo obsoleto. Ver comentário no `finally` de `connect()`.
    this._connected.set(false);
  }
}

/**
 * Cria a conexão SSE autenticada de um consumidor. Chame num contexto de injeção (campo de um
 * `@Injectable`): a conexão observa a identidade com `effect()` e se encerra com o `DestroyRef`
 * de quem a criou.
 */
export function createAuthenticatedEventStream(
  options: AuthenticatedEventStreamOptions
): AuthenticatedEventStream {
  assertInInjectionContext(createAuthenticatedEventStream);
  return new AuthenticatedEventStreamConnection(options);
}
